import { MutableRefObject, useEffect, useReducer, useRef } from "react";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { ExtractRouteParams, generatePath, RouteComponentProps } from "react-router";
import { components } from "src/__generated__/api";
import { AUTH_URLS } from "src/pages/authenticated/Routes";
import { ScorePage } from "src/pages/authenticated/ScoreTemplates";

import { AlertError, ContentSingleColumn, QUERY_CLIENT, useModal } from "~components";
import {
  ModalSkillBuilderFeedback,
  SkillBuilderPage,
  type FeedbackQuestion,
} from "~skillBuilder/components";
import {
  computeGroupsState,
  configurationVersions,
  getConfiguration,
  SkillBuilderConfiguration,
  type Task,
  type Version,
} from "~skillBuilder/utils";
import { GET, POST, replacePath, SkillBuilderPayload } from "~utils";

import { TaskPageReducer, TaskPageReducerState } from "./Task.page.reducer";
import { TaskPageBase } from "./TaskPageBase";

type SkillBuilderTaskPageProps = RouteComponentProps<
  ExtractRouteParams<(typeof AUTH_URLS)["skillBuilderTask"], string>
>;

/***** Component *****/
export function SkillBuilderTaskPage(props: SkillBuilderTaskPageProps) {
  const { history, match } = props;
  const { version, task: taskIndex, step: stepIndex } = match.params;

  /***** State *****/

  // avoid race condition of 2 instances of the modal being rendered
  // when opened resulting in user needed to "close" the modal twice
  const isModalRendered = useRef(false);
  // store the quiz last step's index so that when feedback modal
  // is opened and step is completed we can keep user on the last
  // step when the feedback modal is displayed
  const quizLastStepIndex = useRef<number | undefined>();

  // avoid duplicate POST calls (in dev mode due to useReducer rendered twice)
  const isSubmitFnInitialized = useRef(false);

  // handle feedback error specifically since we want to show an error in the
  // modal if attempting to save feedback results in failure and upon close of
  // the modal avoid displaying the actual POST error on the Task Step
  const isSaveFeedbackError = useRef(false);

  // store the latest computed completion status of all groups and tasks
  // used to determine what task the user should go to next
  const groupsComputedRef = useRef<ReturnType<typeof computeGroupsState>>([]);

  // flatten out all tasks once config is loaded so that we
  // can use during refresh to determine the next task to navigate to
  const flatTaskListRef = useRef<Task[]>([]);

  // Note: in React strict mode `useReducer` is rendered twice
  // https://react.dev/reference/react/useReducer#my-reducer-or-initializer-function-runs-twice
  // Note: fns that are initialized via useReducer and attempt to reference variables directly within
  // this component will be closed over leading to stale values. Therefore, either pass via `payload`
  // or call out to another function within this component that is not initialized via useReducer to
  // access the variable value at the time in which it is needed
  const [state, dispatch] = useReducer(TaskPageReducer, {
    // Setting the initial state to the URL value if it exists
    stepIndex: {
      initial: quizLastStepIndex.current
        ? quizLastStepIndex.current
        : stepIndex
          ? parseInt(stepIndex)
          : undefined,
    },
    // Set to true since we don't have the `taskComputed` field yet
    isLoading: true,
    // Handlers
    onClose: handleClose,
    onSubmit: handleSubmit,
    onStepChange: handleStepChange,
    onQuizLastStep: handleQuizLastStep,
  } satisfies TaskPageReducerState);

  /***** Queries *****/
  const {
    data: subDistrictData,
    isSuccess: isSubDistrictDataSuccess,
    error: districtDataError,
  } = useQuery({
    queryKey: ["fetchSubDistricts"],
    queryFn: () => GET("/api/sub/skillbuilder-districts"),
    /**
     * Data is considered stale after 1 hour
     * Data stays in cache for 2 hours
     * Users see immediate data on mount (even if stale)
     * Fresh data is fetched in the background and cache updated
     * UI automatically updates when fresh data arrives
     * Memory is eventually cleaned up after 2 hours of inactivity
     */
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
  });

  // Determine what district configuration(s) are available to load for this sub
  const districtIds = subDistrictData?.data.associatedDistrictIds;
  const districtConfigurations = districtIds?.filter(
    (districtId): districtId is Version => districtId in configurationVersions,
  );

  const configurationQuery = {
    queryKey: ["skillBuilderConfiguration", version],
    queryFn: () => getConfiguration(version as Version),
    // Cache the configuration for the lifetime of the application since this
    // only changes on new deployments which will require an application refresh.
    // This will avoid all future calls using this key from making a network request.
    staleTime: Infinity,
  };

  const districtQueries =
    districtConfigurations && districtConfigurations.length > 0
      ? districtConfigurations?.map((districtId) => ({
          queryKey: ["skillBuilderConfiguration", districtId],
          queryFn: () => getConfiguration(districtId),
          staleTime: Infinity,
          enabled: isSubDistrictDataSuccess,
        }))
      : null;

  let queries = [];
  if (!districtQueries) {
    queries = [configurationQuery];
  } else {
    queries = [configurationQuery, ...districtQueries];
  }

  const { data: config, errors: configErrors } = useQueries({
    queries,
    // Note: using `combine` results in not being able to access other query properties, such as error
    // https://tanstack.com/query/v5/docs/framework/react/reference/useQueries#combine
    // therefore specifically returning these to be able to update UI accordingly
    combine: (results) => {
      const versionConfiguration = results[0].data as SkillBuilderConfiguration | undefined;
      const districtConfigurationResults = results.slice(1);
      const districtConfigurations = districtConfigurationResults
        .map((config) => config.data as SkillBuilderConfiguration | undefined)
        .filter((config): config is SkillBuilderConfiguration => config !== undefined);

      if (!versionConfiguration) {
        return {
          data: undefined,
          // `filter(Boolean)` to remove `null` when error does not exist
          errors: results.map((res) => res.error).filter(Boolean),
        };
      }

      const groups = [
        ...versionConfiguration.groups,
        ...districtConfigurations.map((config) => config.groups).flat(),
      ];

      const task = groups.flatMap((group) => group.tasks)[parseInt(taskIndex)];
      const taskGroup = groups.find((g) => g.tasks.find((t) => t.uniqueName === task.uniqueName));

      return {
        data: {
          task,
          feedback: taskGroup?.feedback,
          taskGroup,
          groups,
        },
      };
    },
  });

  // Save the task in state
  useEffect(() => {
    if (config?.task) {
      dispatch({ type: "SET_TASK", payload: { task: config.task } });
    }
  }, [config?.task, match.params.task]);

  // Get the skill builder attributes that are used to calculate the progress of each Task
  const { data: skillBuilderAttributes, error: attributesError } = useQuery({
    queryKey: ["skillBuilderAttributes"],
    queryFn: () => GET("/api/sub/skillbuilder"),
    // Since the response is `{ data: [...skillBuilderAttribute] }` we can clean up the response
    select: (data) => data?.data.map((attribute) => ({ ...attribute })),
  });

  // Save the skill builder attributes in state
  useEffect(
    () =>
      skillBuilderAttributes &&
      dispatch({
        type: "SET_SKILL_BUILDER_ATTRIBUTES",
        payload: { skillBuilderAttributes, quizLastStep: quizLastStepIndex.current },
      }),
    // Using `JSON.stringify` to compare the array of skill builder attributes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(skillBuilderAttributes)],
  );

  // Once config and attributes are in, compute group state and store in a ref.
  // This lets us access the latest task status without triggering re-renders.
  useEffect(() => {
    if (config?.groups && skillBuilderAttributes) {
      groupsComputedRef.current = computeGroupsState({
        groups: config.groups,
        syncedSkillBuilderAttributes: skillBuilderAttributes,
      });

      // flatten all tasks once so we don't keep recomputing on every step change
      flatTaskListRef.current = config.groups.flatMap((g) => g.tasks);
    }
  }, [config?.groups, skillBuilderAttributes]);

  /***** Mutations *****/

  const { mutateAsync: submitSkillBuilderAnswers, error: saveError } = useMutation<
    { data: components["schemas"]["SkillBuilderAttr"][] },
    AxiosError,
    SkillBuilderPayload
  >({
    mutationKey: ["submitSkillBuilderAnswers"],
    mutationFn: (answers) => POST("/api/sub/skillbuilder", answers),
    onSuccess: (newSkillBuilderAttribute) => {
      // Update the cache with the new skill builder attributes
      QUERY_CLIENT.setQueryData(["skillBuilderAttributes"], newSkillBuilderAttribute);

      // Recompute all task/group state
      // needed to figure out what the next incomplete task is across groups
      groupsComputedRef.current = computeGroupsState({
        groups: config?.groups ?? [],
        syncedSkillBuilderAttributes: newSkillBuilderAttribute.data,
      });

      // Reset handle submit so function body will execute
      isSubmitFnInitialized.current = false;
      // If we are on the last step in the task then we have 2 flows:
      // - 1) if NOT a task quiz step then return to tasks page
      // - 2) if IS a task quiz step then show feedback modal
      const isLastStepInTask = calcIsLastStepInTask(state.stepIndex?.current);
      if (isLastStepInTask && !config?.task.isQuiz) {
        // If task is complete and not a quiz, go to next task if available
        handleClose({ goToNextTask: true });
      }
      if (isLastStepInTask && config?.task.isQuiz) {
        // keep user on last step of quiz task
        quizLastStepIndex.current = state.stepIndex?.current;
        // open feedback modal
        // Note: we don't need to check if feedback modal is complete here since we expect the user
        // to not have seen the feedback modal yet since they are now just completing the last step
        // in the task quiz which is when they are able to see the modal
        // Note: we also need to have code in the task reducer since this only will trigger upon completion
        // of the last task quiz step. The user may close the modal without completing and come back to the
        // last task quiz step at any time so should be able to view the feedback modal until it is completed
        openModal();
      }
    },
  });

  /******** Feedback Modal *********/

  const { openModal, closeModal } = useModal({
    component: (
      <ModalSkillBuilderFeedback
        onDismiss={() => {
          isModalRendered.current = false;
          quizLastStepIndex.current = undefined;
          // close modal
          closeModal();
          // navigate to tasks page
          handleClose({ goToNextTask: false });
        }}
        questions={config?.feedback || []}
        onConfirm={async (answers) => {
          // save answers
          await submitSkillBuilderAnswers(answers)
            .then(() => {
              // close modal
              closeModal();
              // navigate to tasks page
              handleClose({ goToNextTask: false });
            })
            // set error
            .catch(() => {
              isSaveFeedbackError.current = true;
            });
        }}
        hasApiError={saveError}
      />
    ),
    modalOptions: { cssClass: "modal-skill-builder-feedback" },
  });

  /***** Handlers *****/

  function calcIsLastStepInTask(currentStep?: number) {
    // null check since currentStep could be 0
    if (currentStep !== null && config?.task.steps) {
      if (currentStep === config.task.steps.length - 1) {
        return true;
      }
      return false;
    }
    return false;
  }

  function handleSubmit(state: Parameters<TaskPageReducerState["onSubmit"]>[0]) {
    if (!isSubmitFnInitialized.current) {
      isSubmitFnInitialized.current = true;
      return submitSkillBuilderAnswers(
        // @ts-expect-error - This is having a hard time handing the
        // discriminant union type.
        state.unsyncedSkillBuilderAttributes.map((usba) => ({
          attribute: usba.attribute,
          value: usba.value,
        })),
      );
    }
  }

  // Passing currentTask here since config.task can be
  // out of sync/undefined right after a reducer update
  // or refresh or when the task is changed.
  // This makes sure the URL reflects the
  // actual task we're on.
  function handleStepChange(newStepIndex: number, currentTask?: Task) {
    const taskToUse = currentTask ?? config?.task;
    if (!taskToUse) return;

    const targetTaskIndex = flatTaskListRef.current.findIndex(
      (t) => t.uniqueName === taskToUse.uniqueName,
    );
    if (targetTaskIndex === -1) return;

    const newUrl = generatePath(AUTH_URLS.skillBuilderTask, {
      version,
      task: targetTaskIndex.toString(),
      step: newStepIndex.toString(),
    });

    replacePath(newUrl);
  }

  type HandleCloseParams = {
    goToNextTask: boolean;
  };

  function handleClose(params: HandleCloseParams) {
    const { goToNextTask } = params;
    if (goToNextTask) {
      // Try to get the path to the next incomplete task using the current computed group state
      const nextTaskPath = getNextIncompleteTaskPath(groupsComputedRef, version, config);
      if (nextTaskPath) {
        // If a next task was found, redirect to it
        history.push(nextTaskPath);
        return;
      }
    }

    // send user to the tasks page
    history.push(generatePath(AUTH_URLS.skillBuilderTasks, { version }), {
      groupName: config?.taskGroup?.name,
    });
  }

  function handleQuizLastStep(isFeedbackComplete: boolean) {
    // if user has already completed this group's feedback then go back to tasks
    if (isFeedbackComplete) {
      handleClose({ goToNextTask: false });
      return;
    }
    // otherwise show feedback modal
    // note: avoid initializing more than 1 modal in
    // dev mode due to useReducer being rendered twice
    if (!isModalRendered.current) {
      isModalRendered.current = true;
      openModal();
    }
  }

  /***** Render *****/

  if ((configErrors && configErrors.length > 0) || attributesError || districtDataError) {
    return (
      <SkillBuilderPage>
        <ContentSingleColumn>
          <AlertError />
        </ContentSingleColumn>
      </SkillBuilderPage>
    );
  }

  if (!state.taskComputed || state.stepIndex?.current === undefined) {
    return <SkillBuilderPage isLoading={true} />;
  }

  // Default to false to avoid console errors if step data isn't ready yet
  const isShowPreviousButton =
    config?.task?.steps[state.stepIndex.current]?.showPreviousButton ?? false;

  return (
    <ScorePage title={config?.taskGroup?.name || ""}>
      <ContentSingleColumn>
        {!isSaveFeedbackError.current && saveError && <AlertError />}
        <TaskPageBase
          taskComputed={state.taskComputed}
          stepIndex={state.stepIndex?.current}
          isLoading={state.isLoading}
          isReadOnly={state.isReadOnly}
          onAnswerChange={(skillBuilderAttribute) =>
            dispatch({
              type: "UPDATE_ANSWER",
              payload: { skillBuilderAttribute },
            })
          }
          onNextButtonClick={() =>
            dispatch({ type: "NEXT_BUTTON", payload: { feedback: config?.feedback } })
          }
          onPrevButtonClick={() =>
            dispatch({ type: "PREV_BUTTON", payload: { isShowPreviousButton } })
          }
          // send user to the tasks page
          onClose={() => handleClose({ goToNextTask: false })}
        />
      </ContentSingleColumn>
    </ScorePage>
  );
}

export type ConfigForNextTask = {
  task: Task;
  groups: Array<{ tasks: Task[] }>;
  feedback?: Array<FeedbackQuestion>;
  taskGroup?: { name: string };
};

export function getNextIncompleteTaskPath(
  groupsComputedRef: MutableRefObject<ReturnType<typeof computeGroupsState>>,
  version: string,
  config?: ConfigForNextTask,
): string | null {
  if (!config || groupsComputedRef.current.length === 0) {
    return null;
  }

  const { task, groups } = config;

  // 1. Find the current group and task index
  const currentGroupIndex = groups.findIndex((group) =>
    group.tasks.some((t) => t.uniqueName === task.uniqueName),
  );
  if (currentGroupIndex === -1) return null;

  const currentGroup = groups[currentGroupIndex];
  const currentGroupComputed = groupsComputedRef.current[currentGroupIndex];
  const currentTaskIndex = currentGroup.tasks.findIndex((t) => t.uniqueName === task.uniqueName);
  if (currentTaskIndex === -1) return null;

  // 2. Look for next incomplete task in current group
  for (let i = currentTaskIndex + 1; i < currentGroupComputed.tasks.length; i++) {
    const nextTaskComputed = currentGroupComputed.tasks[i];

    // Skip if the task is completed or locked
    if (nextTaskComputed.status === "COMPLETED" || nextTaskComputed.status === "LOCKED") continue;

    // Found a valid next task
    const taskIndex =
      groups.slice(0, currentGroupIndex).reduce((acc, group) => acc + group.tasks.length, 0) + i;

    return generatePath(AUTH_URLS.skillBuilderTask, {
      version,
      task: taskIndex.toString(),
    });
  }

  // No more incomplete tasks in this group
  // let caller decide where to route (e.g. back to tasks or next task)
  return null;
}
